/**
 * Onboarding Management Service
 * CRUD operations and business logic for white-glove customer onboarding
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  arrayUnion,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  Onboarding,
  OnboardingStatus,
  OnboardingStep,
  OnboardingBlocker,
  OnboardingActivity,
  BlockerSeverity,
  ActivityType,
  OnboardingStats,
  initializeOnboardingSteps,
  calculateProgress,
  calculateTimeToValue,
  calculateStepDuration,
  getNPSCategory,
  getNextStep,
} from '../types/onboarding';

const ONBOARDINGS_COLLECTION = 'onboardings';

/**
 * Get a single onboarding by ID
 */
export async function getOnboarding(id: string): Promise<Onboarding> {
  const docRef = doc(db, ONBOARDINGS_COLLECTION, id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Onboarding not found');
  }

  return { id: docSnap.id, ...docSnap.data() } as Onboarding;
}

/**
 * Get all onboardings
 */
export async function getOnboardings(): Promise<{ onboardings: Onboarding[]; total: number }> {
  const q = query(
    collection(db, ONBOARDINGS_COLLECTION),
    orderBy('createdAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  const onboardings: Onboarding[] = querySnapshot.docs.map(
    (doc) => ({ id: doc.id, ...doc.data() } as Onboarding)
  );

  return {
    onboardings,
    total: onboardings.length,
  };
}

/**
 * Get onboardings by status
 */
export async function getOnboardingsByStatus(status: OnboardingStatus): Promise<Onboarding[]> {
  const q = query(
    collection(db, ONBOARDINGS_COLLECTION),
    where('status', '==', status),
    orderBy('createdAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Onboarding));
}

/**
 * Get onboardings assigned to a specific user
 */
export async function getOnboardingsByAssignee(userId: string): Promise<Onboarding[]> {
  const q = query(
    collection(db, ONBOARDINGS_COLLECTION),
    where('assignedTo', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Onboarding));
}

/**
 * Create a new onboarding from a closed-won lead
 */
export async function createOnboarding(
  leadId: string,
  customerId: string,
  customerName: string,
  customerEmail: string,
  companyName: string,
  doorCount: number,
  contractValue: number,
  targetCompletionDate: Date,
  createdBy: string,
  createdByName: string,
  contractId?: string
): Promise<string> {
  const now = Timestamp.now();
  const targetDate = Timestamp.fromDate(targetCompletionDate);

  const onboardingData: Omit<Onboarding, 'id'> = {
    leadId,
    customerId,
    ...(contractId && { contractId }), // Only include if defined
    customerName,
    customerEmail,
    companyName,
    doorCount,
    contractValue,
    status: 'not_started',
    currentStep: 'kickoff',
    progress: 0,
    targetCompletionDate: targetDate,
    steps: initializeOnboardingSteps(),
    blockers: [],
    activities: [
      {
        id: `activity-${Date.now()}`,
        type: 'milestone',
        subject: 'Onboarding created',
        description: `Onboarding created from closed-won lead. Target completion: ${targetCompletionDate.toLocaleDateString()}`,
        performedBy: createdBy,
        performedByName: createdByName,
        timestamp: now,
      },
    ],
    createdAt: now,
    createdBy,
    createdByName,
    updatedAt: now,
    updatedBy: createdBy,
    updatedByName: createdByName,
  };

  const docRef = await addDoc(collection(db, ONBOARDINGS_COLLECTION), onboardingData);
  return docRef.id;
}

/**
 * Update onboarding fields
 */
export async function updateOnboarding(
  id: string,
  data: Partial<Onboarding>,
  updatedBy: string,
  updatedByName: string
): Promise<void> {
  const docRef = doc(db, ONBOARDINGS_COLLECTION, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
    updatedBy,
    updatedByName,
  });
}

/**
 * Assign onboarding to a specialist
 */
export async function assignOnboarding(
  id: string,
  userId: string,
  userName: string,
  assignedBy: string,
  assignedByName: string
): Promise<void> {
  const docRef = doc(db, ONBOARDINGS_COLLECTION, id);

  const activity: OnboardingActivity = {
    id: `activity-${Date.now()}`,
    type: 'note',
    subject: 'Onboarding assigned',
    description: `Assigned to ${userName}`,
    performedBy: assignedBy,
    performedByName: assignedByName,
    timestamp: Timestamp.now(),
  };

  await updateDoc(docRef, {
    assignedTo: userId,
    assignedToName: userName,
    activities: arrayUnion(activity),
    updatedAt: Timestamp.now(),
    updatedBy: assignedBy,
    updatedByName: assignedByName,
  });
}

/**
 * Update overall onboarding status
 */
export async function updateOnboardingStatus(
  id: string,
  status: OnboardingStatus,
  updatedBy: string,
  updatedByName: string,
  notes?: string
): Promise<void> {
  const docRef = doc(db, ONBOARDINGS_COLLECTION, id);
  const onboarding = await getOnboarding(id);

  const activity: OnboardingActivity = {
    id: `activity-${Date.now()}`,
    type: 'milestone',
    subject: `Status changed to ${status}`,
    description: notes,
    performedBy: updatedBy,
    performedByName: updatedByName,
    timestamp: Timestamp.now(),
  };

  const updateData: any = {
    status,
    activities: arrayUnion(activity),
    updatedAt: Timestamp.now(),
    updatedBy,
    updatedByName: updatedByName,
  };

  // If completed, set completedDate
  if (status === 'completed' && !onboarding.completedDate) {
    updateData.completedDate = Timestamp.now();
    
    // Calculate time-to-value if we have kickoff date
    if (onboarding.kickoffDate) {
      const ttv = calculateTimeToValue(onboarding.kickoffDate, Timestamp.now());
      if (ttv !== undefined) {
        updateData.timeToValue = ttv;
      }
    }
  }

  await updateDoc(docRef, updateData);
}

/**
 * Start a specific onboarding step
 */
export async function startStep(
  id: string,
  step: OnboardingStep,
  startedBy: string,
  startedByName: string,
  assignedTo?: string,
  assignedToName?: string
): Promise<void> {
  const docRef = doc(db, ONBOARDINGS_COLLECTION, id);
  const onboarding = await getOnboarding(id);

  // Find the step to update
  const stepIndex = onboarding.steps.findIndex((s) => s.step === step);
  if (stepIndex === -1) {
    throw new Error(`Step ${step} not found`);
  }

  // Update the step
  const updatedSteps = [...onboarding.steps];
  updatedSteps[stepIndex] = {
    ...updatedSteps[stepIndex],
    status: 'in_progress',
    startedAt: Timestamp.now(),
    assignedTo,
    assignedToName,
  };

  // Add activity
  const activity: OnboardingActivity = {
    id: `activity-${Date.now()}`,
    type: 'milestone',
    subject: `Started: ${step}`,
    description: assignedTo ? `Assigned to ${assignedToName}` : undefined,
    performedBy: startedBy,
    performedByName: startedByName,
    timestamp: Timestamp.now(),
  };

  const updateData: any = {
    steps: updatedSteps,
    currentStep: step,
    activities: arrayUnion(activity),
    updatedAt: Timestamp.now(),
    updatedBy: startedBy,
    updatedByName: startedByName,
  };

  // If this is the kickoff step, set kickoffDate and change status to in_progress
  if (step === 'kickoff') {
    updateData.kickoffDate = Timestamp.now();
    updateData.status = 'in_progress';
  }

  await updateDoc(docRef, updateData);
}

/**
 * Complete a specific onboarding step
 */
export async function completeStep(
  id: string,
  step: OnboardingStep,
  completedBy: string,
  completedByName: string,
  notes?: string
): Promise<void> {
  const docRef = doc(db, ONBOARDINGS_COLLECTION, id);
  const onboarding = await getOnboarding(id);

  // Find the step to update
  const stepIndex = onboarding.steps.findIndex((s) => s.step === step);
  if (stepIndex === -1) {
    throw new Error(`Step ${step} not found`);
  }

  const stepDetail = onboarding.steps[stepIndex];

  // Check if all required checklist items are completed
  const incompleteRequired = stepDetail.checklist.filter(
    (item) => item.isRequired && !item.isCompleted
  );
  if (incompleteRequired.length > 0) {
    throw new Error(
      `Cannot complete step. ${incompleteRequired.length} required checklist items are incomplete.`
    );
  }

  // Calculate duration
  const duration = stepDetail.startedAt
    ? calculateStepDuration(stepDetail.startedAt, Timestamp.now())
    : undefined;

  // Update the step
  const updatedSteps = [...onboarding.steps];
  updatedSteps[stepIndex] = {
    ...updatedSteps[stepIndex],
    status: 'completed',
    completedAt: Timestamp.now(),
    durationDays: duration,
    notes: notes || updatedSteps[stepIndex].notes,
  };

  // Calculate new progress
  const progress = calculateProgress(updatedSteps);

  // Add activity
  const activity: OnboardingActivity = {
    id: `activity-${Date.now()}`,
    type: 'milestone',
    subject: `Completed: ${step}`,
    description: notes || `Step completed in ${duration} days`,
    performedBy: completedBy,
    performedByName: completedByName,
    timestamp: Timestamp.now(),
  };

  const updateData: any = {
    steps: updatedSteps,
    progress,
    activities: arrayUnion(activity),
    updatedAt: Timestamp.now(),
    updatedBy: completedBy,
    updatedByName: completedByName,
  };

  // If this is the go_live step, set goLiveDate
  if (step === 'go_live') {
    updateData.goLiveDate = Timestamp.now();
    
    // Calculate time-to-value
    if (onboarding.kickoffDate) {
      const ttv = calculateTimeToValue(onboarding.kickoffDate, Timestamp.now());
      if (ttv !== undefined) {
        updateData.timeToValue = ttv;
      }
    }
  }

  // Auto-advance to next step if not the last step
  const nextStep = getNextStep(step);
  if (nextStep) {
    updateData.currentStep = nextStep;
  } else {
    // If this is the last step, mark onboarding as completed
    updateData.status = 'completed';
    updateData.completedDate = Timestamp.now();
  }

  await updateDoc(docRef, updateData);
}

/**
 * Skip a step (for optional steps only)
 */
export async function skipStep(
  id: string,
  step: OnboardingStep,
  reason: string,
  skippedBy: string,
  skippedByName: string
): Promise<void> {
  const docRef = doc(db, ONBOARDINGS_COLLECTION, id);
  const onboarding = await getOnboarding(id);

  // Find the step to update
  const stepIndex = onboarding.steps.findIndex((s) => s.step === step);
  if (stepIndex === -1) {
    throw new Error(`Step ${step} not found`);
  }

  // Update the step
  const updatedSteps = [...onboarding.steps];
  updatedSteps[stepIndex] = {
    ...updatedSteps[stepIndex],
    status: 'skipped',
    notes: reason,
  };

  // Calculate new progress
  const progress = calculateProgress(updatedSteps);

  // Add activity
  const activity: OnboardingActivity = {
    id: `activity-${Date.now()}`,
    type: 'note',
    subject: `Skipped: ${step}`,
    description: reason,
    performedBy: skippedBy,
    performedByName: skippedByName,
    timestamp: Timestamp.now(),
  };

  // Auto-advance to next step
  const nextStep = getNextStep(step);

  await updateDoc(docRef, {
    steps: updatedSteps,
    progress,
    currentStep: nextStep || step,
    activities: arrayUnion(activity),
    updatedAt: Timestamp.now(),
    updatedBy: skippedBy,
    updatedByName: skippedByName,
  });
}

/**
 * Block a step with a blocker
 */
export async function blockStep(
  id: string,
  step: OnboardingStep,
  reason: string,
  severity: BlockerSeverity,
  blockedBy: string,
  blockedByName: string
): Promise<void> {
  const docRef = doc(db, ONBOARDINGS_COLLECTION, id);
  const onboarding = await getOnboarding(id);

  // Find the step to update
  const stepIndex = onboarding.steps.findIndex((s) => s.step === step);
  if (stepIndex === -1) {
    throw new Error(`Step ${step} not found`);
  }

  // Create blocker
  const blocker: OnboardingBlocker = {
    id: `blocker-${Date.now()}`,
    step,
    reason,
    severity,
    reportedAt: Timestamp.now(),
    reportedBy: blockedBy,
    reportedByName: blockedByName,
  };

  // Update the step
  const updatedSteps = [...onboarding.steps];
  updatedSteps[stepIndex] = {
    ...updatedSteps[stepIndex],
    status: 'blocked',
    isBlocked: true,
    blockerReason: reason,
  };

  // Add activity
  const activity: OnboardingActivity = {
    id: `activity-${Date.now()}`,
    type: 'blocker',
    subject: `Blocker reported: ${step}`,
    description: `${severity.toUpperCase()}: ${reason}`,
    performedBy: blockedBy,
    performedByName: blockedByName,
    timestamp: Timestamp.now(),
    metadata: { severity, blockerId: blocker.id },
  };

  await updateDoc(docRef, {
    steps: updatedSteps,
    status: 'blocked',
    blockers: arrayUnion(blocker),
    activities: arrayUnion(activity),
    updatedAt: Timestamp.now(),
    updatedBy: blockedBy,
    updatedByName: blockedByName,
  });
}

/**
 * Resolve a blocker
 */
export async function resolveBlocker(
  id: string,
  blockerId: string,
  resolution: string,
  resolvedBy: string,
  resolvedByName: string
): Promise<void> {
  const docRef = doc(db, ONBOARDINGS_COLLECTION, id);
  const onboarding = await getOnboarding(id);

  // Find the blocker
  const blockerIndex = onboarding.blockers.findIndex((b) => b.id === blockerId);
  if (blockerIndex === -1) {
    throw new Error(`Blocker ${blockerId} not found`);
  }

  const blocker = onboarding.blockers[blockerIndex];

  // Update blocker
  const updatedBlockers = [...onboarding.blockers];
  updatedBlockers[blockerIndex] = {
    ...blocker,
    resolvedAt: Timestamp.now(),
    resolvedBy,
    resolvedByName,
    resolution,
  };

  // Find the step
  const stepIndex = onboarding.steps.findIndex((s) => s.step === blocker.step);
  if (stepIndex !== -1) {
    const updatedSteps = [...onboarding.steps];
    updatedSteps[stepIndex] = {
      ...updatedSteps[stepIndex],
      status: 'in_progress',
      isBlocked: false,
      blockerResolvedAt: Timestamp.now(),
    };

    // Check if there are any remaining active blockers
    const activeBlockers = updatedBlockers.filter((b) => !b.resolvedAt);
    const newStatus = activeBlockers.length > 0 ? 'blocked' : 'in_progress';

    // Add activity
    const activity: OnboardingActivity = {
      id: `activity-${Date.now()}`,
      type: 'blocker',
      subject: `Blocker resolved: ${blocker.step}`,
      description: resolution,
      performedBy: resolvedBy,
      performedByName: resolvedByName,
      timestamp: Timestamp.now(),
      metadata: { blockerId, originalReason: blocker.reason },
    };

    await updateDoc(docRef, {
      steps: updatedSteps,
      blockers: updatedBlockers,
      status: newStatus,
      activities: arrayUnion(activity),
      updatedAt: Timestamp.now(),
      updatedBy: resolvedBy,
      updatedByName: resolvedByName,
    });
  } else {
    // Just update blocker if step not found
    await updateDoc(docRef, {
      blockers: updatedBlockers,
      updatedAt: Timestamp.now(),
      updatedBy: resolvedBy,
      updatedByName: resolvedByName,
    });
  }
}

/**
 * Update a checklist item
 */
export async function updateStepChecklist(
  id: string,
  step: OnboardingStep,
  checklistItemId: string,
  isCompleted: boolean,
  completedBy: string,
  completedByName: string,
  notes?: string
): Promise<void> {
  const docRef = doc(db, ONBOARDINGS_COLLECTION, id);
  const onboarding = await getOnboarding(id);

  // Find the step
  const stepIndex = onboarding.steps.findIndex((s) => s.step === step);
  if (stepIndex === -1) {
    throw new Error(`Step ${step} not found`);
  }

  // Find the checklist item
  const checklistIndex = onboarding.steps[stepIndex].checklist.findIndex(
    (item) => item.id === checklistItemId
  );
  if (checklistIndex === -1) {
    throw new Error(`Checklist item ${checklistItemId} not found`);
  }

  // Update checklist item
  const updatedSteps = [...onboarding.steps];
  const updatedChecklist = [...updatedSteps[stepIndex].checklist];
  updatedChecklist[checklistIndex] = {
    ...updatedChecklist[checklistIndex],
    isCompleted,
    completedAt: isCompleted ? Timestamp.now() : undefined,
    completedBy: isCompleted ? completedBy : undefined,
    completedByName: isCompleted ? completedByName : undefined,
    notes: notes || updatedChecklist[checklistIndex].notes,
  };

  updatedSteps[stepIndex] = {
    ...updatedSteps[stepIndex],
    checklist: updatedChecklist,
  };

  await updateDoc(docRef, {
    steps: updatedSteps,
    updatedAt: Timestamp.now(),
    updatedBy: completedBy,
    updatedByName: completedByName,
  });
}

/**
 * Add an activity log entry
 */
export async function addOnboardingActivity(
  id: string,
  type: ActivityType,
  subject: string,
  description: string,
  performedBy: string,
  performedByName: string,
  metadata?: Record<string, any>
): Promise<void> {
  const docRef = doc(db, ONBOARDINGS_COLLECTION, id);

  const activity: OnboardingActivity = {
    id: `activity-${Date.now()}`,
    type,
    subject,
    description,
    performedBy,
    performedByName,
    timestamp: Timestamp.now(),
    metadata,
  };

  await updateDoc(docRef, {
    activities: arrayUnion(activity),
    updatedAt: Timestamp.now(),
    updatedBy: performedBy,
    updatedByName: performedByName,
  });
}

/**
 * Capture NPS score
 */
export async function captureNPS(
  id: string,
  score: number,
  feedback: string,
  capturedBy: string,
  capturedByName: string
): Promise<void> {
  if (score < 0 || score > 10) {
    throw new Error('NPS score must be between 0 and 10');
  }

  const docRef = doc(db, ONBOARDINGS_COLLECTION, id);

  const category = getNPSCategory(score);

  const activity: OnboardingActivity = {
    id: `activity-${Date.now()}`,
    type: 'milestone',
    subject: 'NPS score received',
    description: `Score: ${score}/10 (${category}). Feedback: ${feedback}`,
    performedBy: capturedBy,
    performedByName: capturedByName,
    timestamp: Timestamp.now(),
    metadata: { npsScore: score, npsCategory: category },
  };

  await updateDoc(docRef, {
    npsScore: score,
    feedback,
    activities: arrayUnion(activity),
    updatedAt: Timestamp.now(),
    updatedBy: capturedBy,
    updatedByName: capturedByName,
  });
}

/**
 * Capture customer satisfaction rating
 */
export async function captureSatisfaction(
  id: string,
  rating: number,
  feedback: string,
  capturedBy: string,
  capturedByName: string
): Promise<void> {
  if (rating < 1 || rating > 5) {
    throw new Error('Satisfaction rating must be between 1 and 5');
  }

  const docRef = doc(db, ONBOARDINGS_COLLECTION, id);

  const activity: OnboardingActivity = {
    id: `activity-${Date.now()}`,
    type: 'milestone',
    subject: 'Satisfaction rating received',
    description: `Rating: ${rating}/5 stars. Feedback: ${feedback}`,
    performedBy: capturedBy,
    performedByName: capturedByName,
    timestamp: Timestamp.now(),
    metadata: { satisfactionRating: rating },
  };

  await updateDoc(docRef, {
    satisfactionRating: rating,
    feedback: feedback || undefined,
    activities: arrayUnion(activity),
    updatedAt: Timestamp.now(),
    updatedBy: capturedBy,
    updatedByName: capturedByName,
  });
}

/**
 * Get onboarding statistics
 */
export async function getOnboardingStats(): Promise<OnboardingStats> {
  const { onboardings } = await getOnboardings();

  // Count by status
  const notStarted = onboardings.filter((o) => o.status === 'not_started').length;
  const inProgress = onboardings.filter((o) => o.status === 'in_progress').length;
  const blocked = onboardings.filter((o) => o.status === 'blocked').length;
  const completed = onboardings.filter((o) => o.status === 'completed').length;
  const cancelled = onboardings.filter((o) => o.status === 'cancelled').length;

  // Calculate time-to-value
  const completedWithTTV = onboardings.filter((o) => o.timeToValue !== undefined);
  const averageTimeToValue =
    completedWithTTV.length > 0
      ? completedWithTTV.reduce((sum, o) => sum + (o.timeToValue || 0), 0) / completedWithTTV.length
      : 0;

  // Calculate average progress
  const averageProgressPercentage =
    onboardings.length > 0
      ? onboardings.reduce((sum, o) => sum + o.progress, 0) / onboardings.length
      : 0;

  // Calculate on-time completion rate
  const completedOnTime = onboardings.filter(
    (o) =>
      o.status === 'completed' &&
      o.completedDate &&
      o.completedDate.toMillis() <= o.targetCompletionDate.toMillis()
  ).length;
  const onTimeCompletionRate = completed > 0 ? (completedOnTime / completed) * 100 : 0;

  // NPS calculations
  const withNPS = onboardings.filter((o) => o.npsScore !== undefined);
  const averageNPS =
    withNPS.length > 0
      ? withNPS.reduce((sum, o) => sum + (o.npsScore || 0), 0) / withNPS.length
      : 0;
  const npsPromoters = withNPS.filter((o) => (o.npsScore || 0) >= 9).length;
  const npsPassives = withNPS.filter((o) => (o.npsScore || 0) >= 7 && (o.npsScore || 0) < 9).length;
  const npsDetractors = withNPS.filter((o) => (o.npsScore || 0) < 7).length;
  const npsResponseRate = onboardings.length > 0 ? (withNPS.length / onboardings.length) * 100 : 0;

  // Satisfaction calculations
  const withSatisfaction = onboardings.filter((o) => o.satisfactionRating !== undefined);
  const averageSatisfaction =
    withSatisfaction.length > 0
      ? withSatisfaction.reduce((sum, o) => sum + (o.satisfactionRating || 0), 0) /
        withSatisfaction.length
      : 0;

  // Step statistics
  const steps: OnboardingStep[] = [
    'kickoff',
    'gmail_setup',
    'property_import',
    'tenant_import',
    'training',
    'testing',
    'go_live',
    'follow_up',
  ];

  const stepStats: OnboardingStats['stepStats'] = {} as any;
  steps.forEach((step) => {
    const stepData = onboardings.flatMap((o) => o.steps.filter((s) => s.step === step));
    const completedSteps = stepData.filter((s) => s.status === 'completed');
    const averageDuration =
      completedSteps.length > 0
        ? completedSteps.reduce((sum, s) => sum + (s.durationDays || 0), 0) / completedSteps.length
        : 0;
    const completionRate = stepData.length > 0 ? (completedSteps.length / stepData.length) * 100 : 0;
    const blockerCount = stepData.filter((s) => s.isBlocked).length;

    stepStats[step] = {
      averageDuration,
      completionRate,
      blockerCount,
    };
  });

  // By specialist
  const bySpecialist: OnboardingStats['bySpecialist'] = {};
  onboardings.forEach((o) => {
    if (o.assignedTo && o.assignedToName) {
      if (!bySpecialist[o.assignedTo]) {
        bySpecialist[o.assignedTo] = {
          name: o.assignedToName,
          activeCount: 0,
          completedCount: 0,
          averageNPS: 0,
          averageTimeToValue: 0,
        };
      }

      if (o.status === 'in_progress' || o.status === 'blocked') {
        bySpecialist[o.assignedTo].activeCount++;
      }
      if (o.status === 'completed') {
        bySpecialist[o.assignedTo].completedCount++;
      }
    }
  });

  // Calculate averages for each specialist
  Object.keys(bySpecialist).forEach((userId) => {
    const specialistOnboardings = onboardings.filter((o) => o.assignedTo === userId);
    const withNPS = specialistOnboardings.filter((o) => o.npsScore !== undefined);
    const withTTV = specialistOnboardings.filter((o) => o.timeToValue !== undefined);

    bySpecialist[userId].averageNPS =
      withNPS.length > 0
        ? withNPS.reduce((sum, o) => sum + (o.npsScore || 0), 0) / withNPS.length
        : 0;
    bySpecialist[userId].averageTimeToValue =
      withTTV.length > 0
        ? withTTV.reduce((sum, o) => sum + (o.timeToValue || 0), 0) / withTTV.length
        : 0;
  });

  // Blocker statistics
  const allBlockers = onboardings.flatMap((o) => o.blockers);
  const activeBlockers = allBlockers.filter((b) => !b.resolvedAt).length;
  const resolvedBlockers = allBlockers.filter((b) => b.resolvedAt).length;

  const blockersByStep: OnboardingStats['blockersByStep'] = {} as any;
  steps.forEach((step) => {
    blockersByStep[step] = allBlockers.filter((b) => b.step === step && !b.resolvedAt).length;
  });

  const blockersBySeverity = {
    low: allBlockers.filter((b) => b.severity === 'low' && !b.resolvedAt).length,
    medium: allBlockers.filter((b) => b.severity === 'medium' && !b.resolvedAt).length,
    high: allBlockers.filter((b) => b.severity === 'high' && !b.resolvedAt).length,
    critical: allBlockers.filter((b) => b.severity === 'critical' && !b.resolvedAt).length,
  };

  return {
    total: onboardings.length,
    notStarted,
    inProgress,
    blocked,
    completed,
    cancelled,
    averageTimeToValue,
    averageProgressPercentage,
    onTimeCompletionRate,
    averageNPS,
    averageSatisfaction,
    npsPromoters,
    npsPassives,
    npsDetractors,
    npsResponseRate,
    stepStats,
    bySpecialist,
    activeBlockers,
    resolvedBlockers,
    blockersByStep,
    blockersBySeverity,
  };
}
